import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Plus, Pencil, Trash2, ShieldCheck, UserPlus, Mail, X } from "lucide-react";
import { fetchAccessPermissions, fetchRoles, createRole, updateRole, deleteRole,
         fetchStaff, createStaff, updateStaff, resendStaffInvite, resetStaffMfa, fetchSecurityEvents, formatApiError } from "@/lib/api";

const inp = "w-full rounded-lg border border-stone-300 bg-white px-3 py-2 text-sm outline-none focus:border-saffron";
const lab = "block text-[11px] font-bold uppercase tracking-wider text-stone-500 mb-1";

export function AccessManager({ token }) {
    const [perms, setPerms] = useState([]);
    const [roles, setRoles] = useState(null);
    const [staff, setStaff] = useState(null);
    const [editing, setEditing] = useState(null); // role being edited or "new"
    const [roleForm, setRoleForm] = useState({ name: "", description: "", permissions: [] });
    const [staffForm, setStaffForm] = useState({ name: "", email: "", role_id: "" });
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        try {
            const [p, r, s] = await Promise.all([fetchAccessPermissions(token), fetchRoles(token), fetchStaff(token)]);
            setPerms(p.permissions);
            setRoles(r.roles);
            setStaff(s.users);
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Could not load access settings");
        }
    }, [token]);
    useEffect(() => { load(); }, [load]);

    const openRole = (role) => {
        setEditing(role ? role.id : "new");
        setRoleForm(role
            ? { name: role.name, description: role.description || "", permissions: [...role.permissions] }
            : { name: "", description: "", permissions: [] });
    };

    const togglePerm = (key) => setRoleForm((f) => ({
        ...f,
        permissions: f.permissions.includes(key) ? f.permissions.filter((p) => p !== key) : [...f.permissions, key],
    }));

    const saveRole = async () => {
        if (!roleForm.name.trim()) { toast.error("Role name is required"); return; }
        if (!roleForm.permissions.length) { toast.error("Pick at least one permission"); return; }
        setBusy(true);
        try {
            if (editing === "new") {
                await createRole(token, roleForm);
                toast.success("Role created");
            } else {
                await updateRole(token, editing, roleForm);
                toast.success("Role updated — applies to its users immediately");
            }
            setEditing(null);
            load();
        } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
        finally { setBusy(false); }
    };

    const toggleRoleActive = async (role) => {
        try {
            await updateRole(token, role.id, { active: !role.active });
            toast.success(role.active ? `Role "${role.name}" deactivated — its users lose that access` : `Role "${role.name}" activated`);
            load();
        } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    };

    const removeRole = async (role) => {
        if (!window.confirm(`Delete the role "${role.name}"?`)) return;
        try {
            await deleteRole(token, role.id);
            toast.success("Role deleted");
            load();
        } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    };

    const inviteStaff = async () => {
        if (!staffForm.name.trim() || !staffForm.email.trim() || !staffForm.role_id) {
            toast.error("Name, email and role are all required"); return;
        }
        setBusy(true);
        try {
            const r = await createStaff(token, staffForm);
            toast.success(r.email_sent
                ? `Invite emailed to ${r.email} — they set their own password via the link`
                : `Account created for ${r.email}, but the invite email failed — use Resend invite`);
            setStaffForm({ name: "", email: "", role_id: "" });
            load();
        } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
        finally { setBusy(false); }
    };

    const changeStaffRole = async (u, role_id) => {
        try {
            await updateStaff(token, u.id, { role_id });
            toast.success(`${u.name} moved to a new role`);
            load();
        } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    };

    const toggleStaffActive = async (u) => {
        if (u.active && !window.confirm(`Deactivate ${u.name}? Their login stops working immediately.`)) return;
        try {
            await updateStaff(token, u.id, { active: !u.active });
            toast.success(u.active ? `${u.name} deactivated` : `${u.name} reactivated`);
            load();
        } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    };

    const resend = async (u) => {
        try {
            const r = await resendStaffInvite(token, u.id);
            toast.success(r.email_sent ? `Fresh set-password link emailed to ${u.email}` : "Email failed — check mail settings");
        } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    };

    const resetMfa = async (u) => {
        if (!window.confirm(`Reset two-factor security for ${u.name}? Their sessions end now and they re-enrol at next login.`)) return;
        try {
            await resetStaffMfa(token, u.id);
            toast.success(`MFA reset for ${u.name} — they'll re-enrol at next login`);
        } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    };

    if (!roles || !staff) return <div className="py-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-saffron" /></div>;

    return (
        <section className="space-y-8" data-testid="access-manager">
            {/* ---------------- Roles ---------------- */}
            <div className="bg-white rounded-2xl border border-stone-300 p-6">
                <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                        <h2 className="font-display font-black text-lg text-navy flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-saffron" /> Roles & Permissions</h2>
                        <p className="text-xs text-stone-500 mt-1">Each role grants access to specific Admin Portal tabs. Changes apply to assigned users immediately.</p>
                    </div>
                    <button onClick={() => openRole(null)} data-testid="role-create-btn"
                        className="inline-flex items-center gap-1.5 rounded-full bg-navy px-4 py-2 text-[11px] font-black uppercase tracking-wider text-white hover:bg-navy-light">
                        <Plus className="h-3.5 w-3.5" /> New role
                    </button>
                </div>

                <div className="mt-4 grid gap-3 md:grid-cols-2">
                    {roles.map((r) => (
                        <div key={r.id} className={`rounded-xl border p-4 ${r.active ? "border-stone-200" : "border-stone-100 bg-stone-50 opacity-70"}`} data-testid={`role-card-${r.id}`}>
                            <div className="flex items-start justify-between gap-2">
                                <div>
                                    <p className="font-bold text-navy">{r.name}
                                        {r.is_template && <span className="ml-2 rounded-full bg-stone-100 px-2 py-0.5 text-[9px] font-black uppercase tracking-wider text-stone-500">Template</span>}
                                    </p>
                                    <p className="text-xs text-stone-500 mt-0.5">{r.description}</p>
                                </div>
                                <span className={`shrink-0 rounded-full px-2 py-0.5 text-[9px] font-black uppercase tracking-wider ${r.active ? "bg-green-100 text-green-700" : "bg-stone-200 text-stone-500"}`}>
                                    {r.active ? "Active" : "Inactive"}
                                </span>
                            </div>
                            <div className="mt-2 flex flex-wrap gap-1">
                                {r.permissions.map((p) => (
                                    <span key={p} className="rounded-full bg-navy/5 text-navy px-2 py-0.5 text-[10px] font-bold">{perms.find((x) => x.key === p)?.label || p}</span>
                                ))}
                                {r.permissions.length === 0 && <span className="text-[10px] text-stone-400">No permissions</span>}
                            </div>
                            <div className="mt-2 flex items-center gap-2 text-[10px] text-stone-400">
                                <span>{r.users_count || 0} user(s)</span>
                                <span>· Updated {new Date(r.updated_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}</span>
                                <span className="ml-auto flex gap-1.5">
                                    <button onClick={() => openRole(r)} data-testid={`role-edit-${r.id}`} className="rounded-full bg-stone-100 px-2.5 py-1 font-black uppercase tracking-wider text-stone-600 hover:bg-stone-200"><Pencil className="h-3 w-3 inline" /> Edit</button>
                                    <button onClick={() => toggleRoleActive(r)} data-testid={`role-active-${r.id}`} className="rounded-full bg-stone-100 px-2.5 py-1 font-black uppercase tracking-wider text-stone-600 hover:bg-stone-200">{r.active ? "Deactivate" : "Activate"}</button>
                                    <button onClick={() => removeRole(r)} data-testid={`role-delete-${r.id}`} className="rounded-full bg-red-50 px-2.5 py-1 font-black uppercase tracking-wider text-red-500 hover:bg-red-100"><Trash2 className="h-3 w-3 inline" /></button>
                                </span>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* ---------------- Staff users ---------------- */}
            <div className="bg-white rounded-2xl border border-stone-300 p-6">
                <h2 className="font-display font-black text-lg text-navy flex items-center gap-2"><UserPlus className="h-5 w-5 text-saffron" /> Staff Users</h2>
                <p className="text-xs text-stone-500 mt-1">Invite staff by email — they receive a link to set their own password. Deactivating blocks their login instantly.</p>

                <div className="mt-4 grid md:grid-cols-[1fr_1fr_1fr_auto] gap-2">
                    <input className={inp} placeholder="Full name" value={staffForm.name} onChange={(e) => setStaffForm({ ...staffForm, name: e.target.value })} data-testid="staff-name" />
                    <input className={inp} placeholder="Email" type="email" value={staffForm.email} onChange={(e) => setStaffForm({ ...staffForm, email: e.target.value })} data-testid="staff-email" />
                    <select className={inp} value={staffForm.role_id} onChange={(e) => setStaffForm({ ...staffForm, role_id: e.target.value })} data-testid="staff-role">
                        <option value="">Choose role…</option>
                        {roles.filter((r) => r.active).map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
                    </select>
                    <button onClick={inviteStaff} disabled={busy} data-testid="staff-invite-btn"
                        className="inline-flex items-center justify-center gap-1.5 rounded-full bg-saffron px-4 py-2 text-[11px] font-black uppercase tracking-wider text-white hover:brightness-95 disabled:opacity-50">
                        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Mail className="h-3.5 w-3.5" />} Send invite
                    </button>
                </div>

                <div className="mt-4 overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead><tr className="text-left text-[10px] font-black uppercase tracking-wider text-stone-400 border-b border-stone-100">
                            <th className="py-2 pr-3">Name</th><th className="py-2 pr-3">Email</th><th className="py-2 pr-3">Role</th><th className="py-2 pr-3">Status</th><th className="py-2">Actions</th>
                        </tr></thead>
                        <tbody>
                            {staff.map((u) => (
                                <tr key={u.id} className="border-b border-stone-50" data-testid={`staff-row-${u.id}`}>
                                    <td className="py-2.5 pr-3 font-bold text-navy">{u.name}
                                        {u.is_super && <span className="ml-2 rounded-full bg-gold/20 text-yellow-700 px-2 py-0.5 text-[9px] font-black uppercase">Super Admin</span>}
                                    </td>
                                    <td className="py-2.5 pr-3 text-stone-500">{u.email}</td>
                                    <td className="py-2.5 pr-3">
                                        {u.is_super ? <span className="text-xs text-stone-400">Full access</span> : (
                                            <select className="rounded-lg border border-stone-200 px-2 py-1 text-xs" value={u.role_id || ""}
                                                onChange={(e) => changeStaffRole(u, e.target.value)} data-testid={`staff-role-select-${u.id}`}>
                                                {roles.filter((r) => r.active || r.id === u.role_id).map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
                                            </select>
                                        )}
                                    </td>
                                    <td className="py-2.5 pr-3">
                                        <span className={`rounded-full px-2 py-0.5 text-[9px] font-black uppercase tracking-wider ${u.active !== false ? "bg-green-100 text-green-700" : "bg-red-100 text-red-600"}`}>
                                            {u.active !== false ? "Active" : "Deactivated"}
                                        </span>
                                    </td>
                                    <td className="py-2.5">
                                        {!u.is_super && (
                                            <div className="flex gap-1.5 flex-wrap">
                                                <button onClick={() => resend(u)} data-testid={`staff-resend-${u.id}`} className="rounded-full bg-stone-100 px-2.5 py-1 text-[9px] font-black uppercase tracking-wider text-stone-600 hover:bg-stone-200">Resend invite</button>
                                                <button onClick={() => resetMfa(u)} data-testid={`staff-reset-mfa-${u.id}`} className="rounded-full bg-amber-50 px-2.5 py-1 text-[9px] font-black uppercase tracking-wider text-amber-700 hover:bg-amber-100">Reset MFA</button>
                                                <button onClick={() => toggleStaffActive(u)} data-testid={`staff-active-${u.id}`} className={`rounded-full px-2.5 py-1 text-[9px] font-black uppercase tracking-wider ${u.active !== false ? "bg-red-50 text-red-500 hover:bg-red-100" : "bg-green-50 text-green-600 hover:bg-green-100"}`}>
                                                    {u.active !== false ? "Deactivate" : "Activate"}
                                                </button>
                                            </div>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* ---------------- Security audit log ---------------- */}
            <SecurityLog token={token} />

            {/* ---------------- Role editor modal ---------------- */}
            {editing && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-navy/60 p-4" data-testid="role-editor">
                    <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl max-h-[90vh] overflow-y-auto">
                        <div className="flex items-start justify-between">
                            <h3 className="font-display font-black text-lg text-navy">{editing === "new" ? "New role" : "Edit role"}</h3>
                            <button onClick={() => setEditing(null)} data-testid="role-editor-close" className="text-stone-400 hover:text-navy"><X className="h-5 w-5" /></button>
                        </div>
                        <div className="mt-4 space-y-3">
                            <div><label className={lab}>Role name</label>
                                <input className={inp} value={roleForm.name} onChange={(e) => setRoleForm({ ...roleForm, name: e.target.value })} placeholder="e.g. Finance Executive" data-testid="role-name" /></div>
                            <div><label className={lab}>Description / purpose</label>
                                <input className={inp} value={roleForm.description} onChange={(e) => setRoleForm({ ...roleForm, description: e.target.value })} placeholder="What this role is responsible for" data-testid="role-desc" /></div>
                            <div>
                                <label className={lab}>Allowed sections</label>
                                <div className="space-y-1.5">
                                    {perms.map((p) => (
                                        <label key={p.key} className="flex items-center gap-2.5 rounded-lg border border-stone-200 px-3 py-2 cursor-pointer hover:border-navy">
                                            <input type="checkbox" checked={roleForm.permissions.includes(p.key)} onChange={() => togglePerm(p.key)}
                                                className="h-4 w-4 accent-[#F26522]" data-testid={`role-perm-${p.key}`} />
                                            <span className="text-sm font-bold text-navy">{p.label}</span>
                                        </label>
                                    ))}
                                </div>
                                <p className="mt-2 text-[10px] text-stone-400">User Access Management itself stays Super-Admin-only and is never assignable.</p>
                            </div>
                            <button onClick={saveRole} disabled={busy} data-testid="role-save"
                                className="w-full rounded-full bg-saffron py-2.5 text-[11px] font-black uppercase tracking-wider text-white hover:brightness-95 disabled:opacity-50">
                                {busy ? <Loader2 className="h-4 w-4 animate-spin inline" /> : editing === "new" ? "Create role" : "Save changes"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </section>
    );
}

const KIND_CHIP = {
    "login-failed": "bg-red-100 text-red-600",
    "login-success-mfa": "bg-green-100 text-green-700",
    "mfa-verify-failed": "bg-red-100 text-red-600",
    "rate-limited": "bg-amber-100 text-amber-700",
    "payment-signature-forged": "bg-red-100 text-red-700",
    "ai-injection-attempt": "bg-purple-100 text-purple-700",
};

function SecurityLog({ token }) {
    const [open, setOpen] = useState(false);
    const [data, setData] = useState(null);
    const loadEvents = async () => {
        try { setData(await fetchSecurityEvents(token)); }
        catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    };
    const toggle = () => { if (!open && !data) loadEvents(); setOpen(!open); };

    return (
        <div className="mt-6 rounded-xl border border-stone-200" data-testid="security-log">
            <button onClick={toggle} data-testid="security-log-toggle" className="flex w-full items-center justify-between px-4 py-3 text-left">
                <span className="flex items-center gap-2 font-display font-black text-sm text-navy"><ShieldCheck className="h-4 w-4 text-saffron" /> Security audit log</span>
                <span className="text-[10px] font-black uppercase tracking-wider text-stone-400">{open ? "Hide" : "Show"} · 180-day retention</span>
            </button>
            {open && (
                <div className="border-t border-stone-100 px-4 py-3">
                    {!data ? <Loader2 className="h-5 w-5 animate-spin text-saffron" /> : (
                        <div className="max-h-72 overflow-y-auto">
                            <table className="w-full text-xs">
                                <thead><tr className="text-left text-[10px] uppercase tracking-wider text-stone-400 border-b border-stone-100">
                                    <th className="py-1.5 pr-2">Time (UTC)</th><th className="py-1.5 pr-2">Event</th><th className="py-1.5 pr-2">Actor</th><th className="py-1.5 pr-2">Detail</th><th className="py-1.5">IP</th>
                                </tr></thead>
                                <tbody>
                                    {data.events.map((e, i) => (
                                        <tr key={i} className="border-b border-stone-50" data-testid={`sec-event-${i}`}>
                                            <td className="py-1.5 pr-2 whitespace-nowrap text-stone-400">{(e.at || "").replace("T", " ").slice(0, 19)}</td>
                                            <td className="py-1.5 pr-2"><span className={`rounded-full px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider ${KIND_CHIP[e.kind] || "bg-stone-100 text-stone-600"}`}>{e.kind}</span></td>
                                            <td className="py-1.5 pr-2 font-bold text-navy">{e.actor || "—"}</td>
                                            <td className="py-1.5 pr-2 text-stone-500 break-all">{e.detail || "—"}</td>
                                            <td className="py-1.5 text-stone-400">{e.ip || "—"}</td>
                                        </tr>
                                    ))}
                                    {!data.events.length && <tr><td colSpan={5} className="py-4 text-center text-stone-400">No security events yet.</td></tr>}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
